import { Add, Check } from "@similarweb/icons";
import { colorsPalettes, elevation } from "@similarweb/styles";
import { EmptyState, ListContentLoader } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { IGoogleAdsAccountOption } from "../hooks/use-google-ads-accounts";
import { useGoogleAdsCampaigns, type IGoogleAdsCampaignListItem } from "../hooks/use-google-ads-campaigns";

// A removed campaign isn't a real candidate to import from — Google Ads' own UI hides
// these by default too, so the picker excludes them rather than letting them be selected.
const REMOVED_CAMPAIGN_STATUS = "REMOVED";

interface IGoogleAdsCampaignsStepHeaderProps {
    account: IGoogleAdsAccountOption;
}

interface IGoogleAdsCampaignsStepProps {
    account: IGoogleAdsAccountOption;
    selectedCampaign: IGoogleAdsCampaignListItem | null;
    onSelectCampaign: (campaign: IGoogleAdsCampaignListItem) => void;
}

interface IGoogleAdsCampaignListItemProps {
    campaign: IGoogleAdsCampaignListItem;
    isSelected: boolean;
    onClick: () => void;
}

export const GoogleAdsCampaignsStepHeader = ({ account }: IGoogleAdsCampaignsStepHeaderProps) => {
    return (
        <Stack gap="4px">
            <Text
                fontVariant="h5_2"
                color={colorsPalettes.carbon["500"]}
            >
                Your Campaigns
            </Text>
            <Text
                fontVariant="caption"
                color={colorsPalettes.carbon["300"]}
            >
                Account: {account.name} ({account.id})
            </Text>
        </Stack>
    );
};

const GoogleAdsCampaignListItemRow = ({ campaign, isSelected, onClick }: IGoogleAdsCampaignListItemProps) => {
    return (
        <li>
            <Flex
                as="button"
                type="button"
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                onClick={onClick}
                padding="8px 12px"
                transition="box-shadow 0.2s ease-in-out"
                borderRadius="3px"
                cursor="pointer"
                textAlign="left"
                whenHover={{ boxShadow: elevation.Z1 }}
                background={isSelected ? colorsPalettes.blue["50"] : colorsPalettes.carbon["50"]}
                border={`1px solid ${isSelected ? colorsPalettes.blue["400"] : colorsPalettes.carbon["50"]}`}
                aria-pressed={isSelected}
                data-automation={`campaign-wizard-google-ads-campaign-${campaign.id}`}
            >
                <Stack gap="2px">
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.carbon["500"]}
                    >
                        {campaign.name}
                    </Text>
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon["300"]}
                    >
                        ID: {campaign.id} · Status: {campaign.status}
                    </Text>
                </Stack>
                <Flex
                    inline
                    alignItems="center"
                    gap="4px"
                    data-automation={`campaign-wizard-google-ads-campaign-select-${campaign.id}`}
                >
                    {isSelected ? (
                        <Check
                            size="xs"
                            color={colorsPalettes.blue["400"]}
                        />
                    ) : (
                        <Add
                            size="xs"
                            color={colorsPalettes.blue["400"]}
                        />
                    )}
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.blue["400"]}
                    >
                        {isSelected ? "Selected" : "Select"}
                    </Text>
                </Flex>
            </Flex>
        </li>
    );
};

export const GoogleAdsCampaignsStep = ({
    account,
    selectedCampaign,
    onSelectCampaign,
}: IGoogleAdsCampaignsStepProps) => {
    const { data: campaigns, isLoading, isError, error } = useGoogleAdsCampaigns(account);
    const selectableCampaigns = (campaigns ?? []).filter((campaign) => campaign.status !== REMOVED_CAMPAIGN_STATUS);

    return (
        <Stack
            gap="16px"
            data-automation="campaign-wizard-google-ads-campaigns"
        >
            {isLoading ? (
                <ListContentLoader loaderTitle="Loading your Google Ads campaigns…" />
            ) : isError ? (
                <EmptyState
                    title="Couldn't load Google Ads campaigns"
                    subtitle={error instanceof Error ? error.message : "Unknown error"}
                />
            ) : selectableCampaigns.length > 0 ? (
                <Stack
                    as="ul"
                    gap="8px"
                    data-automation="campaign-wizard-google-ads-campaign-list"
                >
                    {selectableCampaigns.map((campaign) => (
                        <GoogleAdsCampaignListItemRow
                            key={campaign.id}
                            campaign={campaign}
                            isSelected={selectedCampaign?.id === campaign.id}
                            onClick={() => onSelectCampaign(campaign)}
                        />
                    ))}
                </Stack>
            ) : (
                <EmptyState
                    title="No active campaigns found"
                    subtitle="This Google Ads account has no active campaigns to import."
                />
            )}
        </Stack>
    );
};
